/**
 * Delivery-Date Estimate Widget
 * -----------------------------
 * Isolated fetcher + renderer for the panel-managed delivery-estimate
 * envelope (/api/delivery-estimate-config, schemaVersion 1). Computes an
 * estimated arrival date ("Order today — arrives Jul 15 – Jul 17") from
 * per-country order->carrier ship days + carrier->door transit days and
 * renders it into EVERY `.delivery-estimate` anchor on the PDP (Prestige
 * renders product info twice, so multiple anchors are expected). The
 * estimate is product-independent in v1, so no variant/section re-render
 * hooks are needed.
 *
 * Fail-safe by construction: any failure path (no URL global, HTTP error,
 * bad JSON, schema mismatch, master `enabled:false`, unresolvable dates)
 * renders NOTHING and the page is byte-identical to today.
 *
 * Country detection reuses the existing cross-engine source of truth:
 *   1. localStorage['country'] — full English country NAME (e.g.
 *      "United States"), written by recommendations.js and read by
 *      ticking-time-bomb.js / agb-variant-extension.js. Mapped to an
 *      ISO alpha-2 code via the embedded name map below.
 *   2. window.Shopify.country — the buyer's Markets country (ISO-2).
 * Unknown/undetected country -> config defaults + the template rendered
 * WITHOUT the {country} placeholder.
 *
 * Date math is fully client-side (no backend, no libraries):
 *   - "now" is taken in the WAREHOUSE timezone (config.timezone) via
 *     Intl.DateTimeFormat formatToParts;
 *   - orders at/after cutoffHour count from the next day;
 *   - ceil(shipDays) is consumed only on configured workingDays
 *     (ISO Mon=1..Sun=7), then ceil(transitDays) calendar days are added;
 *   - range upper bound = estimate + rangePadDays.
 *
 * Caching mirrors gear-suggestions-config.js: fetched once per page-load,
 * 5-min TTL + ETag in env-namespaced localStorage keys. Endpoint comes
 * from `window.AGB_DELIVERY_ESTIMATE_CONFIG_URL` (set in theme.liquid next
 * to the sibling config globals); region is declared explicitly via the
 * X-Store-Region header from `window.AGB_STORE_REGION`.
 */

(function () {
    'use strict';

    if (typeof window === 'undefined') return;
    if (window.__AGB_DELIVERY_ESTIMATE_LOADED__) return;
    window.__AGB_DELIVERY_ESTIMATE_LOADED__ = true;

    var LOG_PREFIX = '[delivery-estimate]';
    var CONFIG_TTL_MS = 5 * 60 * 1000;

    // ----- Timer + free-shipping text (schemaVersion STAYS 1) ----------------
    // The per-language text is THREE fields:
    //   aboveText      -- message when the order QUALIFIES for free shipping (a
    //                     country rule matches AND price >= its threshold)
    //   belowText      -- message when it does NOT qualify (price below the
    //                     threshold, or price unknown)
    //   noShippingText -- message when there is NO free-shipping context: the
    //                     shopper's country is in NO rule, or free shipping is
    //                     off globally. Delivery-only (no {threshold}). EMPTY =
    //                     hide the widget for those shoppers (old behaviour).
    // Placeholders: {date1} (earliest arrival), {date2} (latest arrival),
    // {threshold} (free-ship threshold in store currency; above/below only),
    // {country} (shopper's 2-letter ISO country CODE, e.g. US/IL/FR). All fields
    // are OPTIONAL + baked-defaulted, so an older
    // envelope carrying none of them still renders. Back-compat: removed keys
    // (label / template / templateSingle / timerText / freeShippingText /
    // freeShippingBelowText, and the top-level showRange) are simply ignored;
    // missing new keys fall through to these baked defaults.
    var DEFAULT_TEXT = {
        en: {
            aboveText: '✔️ Free {country} Shipping — arrives {date1}–{date2}',
            belowText: 'Arrives {date1}–{date2} · Free shipping on orders over {threshold}',
            noShippingText: 'Arrives {date1}–{date2}'
        },
        fr: {
            aboveText: '✔️ Livraison gratuite — livraison entre le {date1} et le {date2}',
            belowText: 'Livraison entre le {date1} et le {date2} · Livraison gratuite dès {threshold}',
            noShippingText: 'Livraison entre le {date1} et le {date2}'
        },
        de: {
            aboveText: '✔️ Kostenloser Versand — Lieferung {date1}–{date2}',
            belowText: 'Lieferung {date1}–{date2} · Kostenloser Versand ab {threshold}',
            noShippingText: 'Lieferung {date1}–{date2}'
        },
        es: {
            aboveText: '✔️ Envío gratis — llega {date1}–{date2}',
            belowText: 'Llega {date1}–{date2} · Envío gratis en pedidos superiores a {threshold}',
            noShippingText: 'Llega {date1}–{date2}'
        }
    };
    // Side countdown-box caption: a BAKED localized default (NOT a config field),
    // resolved by document language with an en fallback.
    var DEFAULT_TIMER_CAPTION = { en: 'Order within', fr: 'Commandez dans', de: 'Bestellen Sie in', es: 'Pide en' };
    var DEFAULT_FREE_SHIPPING_RULES = [{ countries: ['US'], threshold: 99 }];

    // Shared countdown state — a SINGLE setInterval drives every anchor's live
    // timer span; guarded against duplicates and cleared on each re-render.
    var _timerIntervalId = null;
    var _timer = null; // { targetEpoch, spans:[], onExpire }
    // Last valid+enabled config applied — reused by the variant-change handler
    // to re-resolve the message (above/below can flip on price) without re-fetching.
    var _lastCfg = null;

    // Live vs Preview storefront detection (mirrored from
    // gear-suggestions-config.js / cart-quantity-cap.js). Each engine file
    // is self-contained so it can load in any order; preview is the safe
    // default.
    function _agbDetectStorefrontEnv() {
        try {
            var urlForce = new URLSearchParams(location.search).get('storefront_env');
            if (urlForce === 'preview' || urlForce === 'live') return urlForce;
        } catch (_) {}
        try {
            var stored = localStorage.getItem('AGB_StorefrontEnv');
            if (stored === 'preview' || stored === 'live') return stored;
        } catch (_) {}
        var role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
        if (role === 'main') return 'live';
        if (role === 'unpublished' || role === 'development' || role === 'demo') return 'preview';
        var host = (location && location.hostname) || '';
        if (host.indexOf('.shopifypreview.com', host.length - 20) !== -1) return 'preview';
        if (host.indexOf('.myshopify.com', host.length - 14) !== -1) return 'preview';
        if (host === '127.0.0.1' || host === 'localhost') return 'preview';
        try {
            if (new URLSearchParams(location.search).has('preview_theme_id')) return 'preview';
        } catch (_) {}
        return 'preview';
    }

    var DELIV_ENV = _agbDetectStorefrontEnv();
    var LS_CONFIG_KEY = 'AGB_DelivEst_' + DELIV_ENV;
    var LS_CONFIG_ETAG = 'AGB_DelivEstEtag_' + DELIV_ENV;
    var LS_CONFIG_AT = 'AGB_DelivEstFetchedAt_' + DELIV_ENV;

    // Diagnostics print on PREVIEW only — never on live. Honors the panel's
    // dev-logs category toggle window.__AGB_consoleCategories.delivery_estimate
    // (undefined = on, explicit false = silenced — same convention as
    // gear-suggestions / custom-labels).
    function _dbg() {
        try {
            if (DELIV_ENV !== 'preview') return;
            var cats = window.__AGB_consoleCategories;
            if (cats && typeof cats === 'object' && cats.delivery_estimate === false) return;
            console.log.apply(console, arguments);
        } catch (e) {}
    }

    function isFiniteNum(v) {
        return typeof v === 'number' && isFinite(v);
    }

    // ===== Country detection ==================================================

    // Full-name -> ISO alpha-2 map. Keys are lowercased/trimmed names as the
    // existing stack writes them (Intl.DisplayNames 'en' region names via
    // recommendations.js, or GeoLite2-Country names via /api/whoami), plus
    // common aliases. Covers every country plausibly present in the shipping
    // stats; anything unmapped falls back to config defaults.
    var NAME_TO_ISO = {
        'united states': 'US', 'united states of america': 'US', 'usa': 'US',
        'canada': 'CA',
        'united kingdom': 'GB', 'great britain': 'GB', 'uk': 'GB',
        'england': 'GB', 'scotland': 'GB', 'wales': 'GB', 'northern ireland': 'GB',
        'australia': 'AU',
        'japan': 'JP',
        'germany': 'DE',
        'france': 'FR',
        'israel': 'IL',
        'netherlands': 'NL', 'the netherlands': 'NL', 'holland': 'NL',
        'belgium': 'BE',
        'denmark': 'DK',
        'estonia': 'EE',
        'italy': 'IT',
        'lithuania': 'LT',
        'poland': 'PL',
        'spain': 'ES',
        'switzerland': 'CH',
        'south korea': 'KR', 'korea': 'KR', 'republic of korea': 'KR', 'korea, republic of': 'KR',
        'singapore': 'SG',
        'new zealand': 'NZ',
        'austria': 'AT',
        'sweden': 'SE',
        'norway': 'NO',
        'finland': 'FI',
        'ireland': 'IE',
        'portugal': 'PT',
        'mexico': 'MX',
        'brazil': 'BR',
        'south africa': 'ZA',
        'united arab emirates': 'AE', 'uae': 'AE',
        'saudi arabia': 'SA',
        'greece': 'GR',
        'czechia': 'CZ', 'czech republic': 'CZ',
        'slovakia': 'SK',
        'slovenia': 'SI',
        'croatia': 'HR',
        'hungary': 'HU',
        'romania': 'RO',
        'bulgaria': 'BG',
        'latvia': 'LV',
        'luxembourg': 'LU',
        'iceland': 'IS',
        'malta': 'MT',
        'cyprus': 'CY',
        'turkey': 'TR', 'turkiye': 'TR', 'türkiye': 'TR',
        'ukraine': 'UA',
        'georgia': 'GE',
        'serbia': 'RS',
        'hong kong': 'HK', 'hong kong sar china': 'HK',
        'taiwan': 'TW',
        'china': 'CN', 'mainland china': 'CN',
        'thailand': 'TH',
        'malaysia': 'MY',
        'philippines': 'PH',
        'indonesia': 'ID',
        'india': 'IN',
        'vietnam': 'VN',
        'chile': 'CL',
        'argentina': 'AR',
        'colombia': 'CO',
        'peru': 'PE',
        'kuwait': 'KW',
        'qatar': 'QA',
        'bahrain': 'BH',
        'oman': 'OM',
        'jordan': 'JO',
        'egypt': 'EG',
        'nigeria': 'NG',
        'kenya': 'KE'
    };

    /**
     * Resolve the shopper's ISO alpha-2 country code from the SAME sources
     * the rest of the stack uses. Returns '' when undetected/unmapped —
     * the caller then uses config defaults and drops {country}.
     */
    function resolveCountryCode() {
        var raw = '';
        try {
            raw = (localStorage.getItem('country') || '').trim();
            // Legacy poisoned values written as literal strings.
            if (raw === 'null' || raw === 'undefined') raw = '';
        } catch (e) { raw = ''; }
        if (raw) {
            if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
            var mapped = NAME_TO_ISO[raw.toLowerCase()];
            if (mapped) return mapped;
            _dbg(LOG_PREFIX, 'unmapped country name "' + raw + '" -- using defaults');
            return '';
        }
        // Shopify-native buyer signal (Markets country, ISO-2).
        try {
            var sc = window.Shopify && window.Shopify.country;
            if (typeof sc === 'string' && /^[A-Za-z]{2}$/.test(sc.trim())) {
                return sc.trim().toUpperCase();
            }
        } catch (e) {}
        return '';
    }

    // The country the widget acts on: the detected code, or — when detection
    // fails — the configured fallback (cfg.fallbackCountry, ROW default 'US';
    // '' = no fallback). Everything downstream (gate, days, free-ship, display)
    // uses THIS, so an undetected ROW visitor behaves exactly like the fallback.
    function resolveEffectiveCode(cfg) {
        var code = resolveCountryCode();
        if (code) return code;
        var fb = '';
        try {
            fb = (cfg && typeof cfg.fallbackCountry === 'string') ? cfg.fallbackCountry.trim().toUpperCase() : '';
        } catch (e) { fb = ''; }
        return /^[A-Z]{2}$/.test(fb) ? fb : '';
    }

    // Normalize cfg.shipCountries to a clean uppercase ISO-2 array (tolerates a
    // stray comma-string too). Mirrors the backend's shipCountries cleaner.
    function normalizeCountryList(v) {
        var src = [];
        if (Array.isArray(v)) src = v;
        else if (typeof v === 'string') src = v.split(',');
        var out = [];
        for (var i = 0; i < src.length; i++) {
            var key = String(src[i] || '').trim().toUpperCase();
            if (/^[A-Z]{2}$/.test(key) && out.indexOf(key) === -1) out.push(key);
        }
        return out;
    }

    // ALLOW-LIST gate. cfg.shipCountries is the set of ISO-2 codes this region
    // ships to; the widget renders NOTHING for a shopper outside it. An EMPTY
    // list = no restriction (show everywhere). An empty effective code (undetected
    // AND no fallback) with an active list = not shippable (hide).
    function isShippable(cfg, code) {
        var list = normalizeCountryList(cfg && cfg.shipCountries);
        if (!list.length) return true;   // no allow-list configured
        if (!code) return false;         // undetected + no fallback, list active -> hide
        return list.indexOf(code) !== -1;
    }

    // ===== Effective ship/transit days (shared contract semantics) ===========

    /**
     * shipDays    = override.shipDays    ?? computed shipDays    ?? defaultShipDays
     * transitDays = override.transitDays ?? computed transitDays ?? defaultTransitDays
     * Computed values are only trusted when sampleSize >= minSampleSize;
     * overrides always win. Missing entry -> defaults.
     */
    function effectiveDays(cfg, code) {
        var defShip = isFiniteNum(cfg.defaultShipDays) ? cfg.defaultShipDays : 3;
        var defTransit = isFiniteNum(cfg.defaultTransitDays) ? cfg.defaultTransitDays : 5;
        var minSample = isFiniteNum(cfg.minSampleSize) ? cfg.minSampleSize : 30;
        var entry = null;
        if (code && cfg.countries && typeof cfg.countries === 'object' && !Array.isArray(cfg.countries)) {
            var e = cfg.countries[code];
            if (e && typeof e === 'object' && !Array.isArray(e)) entry = e;
        }
        if (!entry) return { ship: defShip, transit: defTransit };
        var ov = (entry.override && typeof entry.override === 'object' && !Array.isArray(entry.override))
            ? entry.override
            : null;
        var sampleOk = isFiniteNum(entry.sampleSize) && entry.sampleSize >= minSample;
        var ship = (ov && isFiniteNum(ov.shipDays)) ? ov.shipDays
            : (sampleOk && isFiniteNum(entry.shipDays)) ? entry.shipDays
            : defShip;
        var transit = (ov && isFiniteNum(ov.transitDays)) ? ov.transitDays
            : (sampleOk && isFiniteNum(entry.transitDays)) ? entry.transitDays
            : defTransit;
        return { ship: ship, transit: transit };
    }

    // ===== Date math ==========================================================

    /**
     * "Now" in the warehouse timezone as {year, month, day, hour}. Falls back
     * to the browser's local time when the configured IANA zone is invalid;
     * returns null when even that fails (caller renders nothing).
     */
    function warehouseNowParts(tz) {
        var opts = { hour12: false, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' };
        var attempts = [];
        if (typeof tz === 'string' && tz) attempts.push(tz);
        attempts.push(null); // browser-local fallback
        for (var a = 0; a < attempts.length; a++) {
            try {
                var o = { hour12: opts.hour12, year: opts.year, month: opts.month, day: opts.day, hour: opts.hour, minute: opts.minute, second: opts.second };
                if (attempts[a]) o.timeZone = attempts[a];
                var parts = new Intl.DateTimeFormat('en-US', o).formatToParts(new Date());
                var out = {};
                for (var i = 0; i < parts.length; i++) {
                    if (parts[i].type !== 'literal') out[parts[i].type] = parseInt(parts[i].value, 10);
                }
                // Some engines report midnight as hour 24 with hour12:false.
                if (out.hour === 24) out.hour = 0;
                if (isFiniteNum(out.year) && isFiniteNum(out.month) && isFiniteNum(out.day) && isFiniteNum(out.hour)) {
                    return out;
                }
            } catch (e) { /* try next */ }
        }
        return null;
    }

    // ISO weekday Mon=1..Sun=7 for a noon-UTC calendar cursor.
    function isoWeekday(d) {
        return ((d.getUTCDay() + 6) % 7) + 1;
    }

    function normalizeWorkingDays(list) {
        var out = [];
        if (Array.isArray(list)) {
            for (var i = 0; i < list.length; i++) {
                var v = list[i];
                if (isFiniteNum(v) && v >= 1 && v <= 7 && out.indexOf(v) === -1) out.push(v);
            }
        }
        return out.length ? out : [1, 2, 3, 4, 5];
    }

    /**
     * Compute {start, end} estimate Dates (noon-UTC calendar cursors — format
     * them with timeZone:'UTC' so the shopper's local zone can't shift the
     * calendar date). Returns null on any failure.
     */
    function computeEstimate(cfg, ship, transit) {
        var parts = warehouseNowParts(cfg.timezone);
        if (!parts) return null;
        var cursor = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12, 0, 0));
        if (isNaN(cursor.getTime())) return null;
        var cutoff = isFiniteNum(cfg.cutoffHour) ? cfg.cutoffHour : 14;
        if (parts.hour >= cutoff) cursor.setUTCDate(cursor.getUTCDate() + 1);
        var working = normalizeWorkingDays(cfg.workingDays);
        var shipTarget = Math.max(0, Math.ceil(ship));
        var consumed = 0;
        var guard = 0; // hard stop so a degenerate config can never loop forever
        while (consumed < shipTarget && guard < 120) {
            cursor.setUTCDate(cursor.getUTCDate() + 1);
            if (working.indexOf(isoWeekday(cursor)) !== -1) consumed += 1;
            guard += 1;
        }
        cursor.setUTCDate(cursor.getUTCDate() + Math.max(0, Math.ceil(transit)));
        var start = new Date(cursor.getTime());
        var pad = isFiniteNum(cfg.rangePadDays) ? Math.max(0, Math.ceil(cfg.rangePadDays)) : 2;
        var end = new Date(cursor.getTime());
        end.setUTCDate(end.getUTCDate() + pad);

        // Global signed calendar-day adjustment (contract v1, optional field —
        // schemaVersion stays 1). Shifts BOTH range bounds equally, AFTER the
        // working-day ship count + transit + rangePadDays. Positive = later /
        // more conservative, negative = earlier / more optimistic. Missing / NaN
        // / non-integer / out-of-[-60,60] all coerce to 0 (back-compat + safety),
        // so an older envelope without deltaDays behaves exactly as before.
        var delta = 0;
        var dd = cfg.deltaDays;
        if (typeof dd === 'string' && dd.trim() !== '') dd = Number(dd);
        if (isFiniteNum(dd)) {
            dd = Math.trunc(dd);
            if (dd >= -60 && dd <= 60) delta = dd;
        }
        if (delta !== 0) {
            start.setUTCDate(start.getUTCDate() + delta);
            end.setUTCDate(end.getUTCDate() + delta);
        }

        // Clamp: a negative delta must never surface a past arrival date. "Today"
        // is the warehouse calendar day (reuse the parts already fetched above —
        // do NOT re-fetch). If the shifted start fell before today, pin it to
        // today and keep end >= start.
        var today = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12, 0, 0));
        if (!isNaN(today.getTime())) {
            if (start.getTime() < today.getTime()) start = new Date(today.getTime());
            if (end.getTime() < start.getTime()) end = new Date(start.getTime());
        }

        return { start: start, end: end };
    }

    function formatDate(d, lang) {
        var locales = [lang || 'en', 'en'];
        for (var i = 0; i < locales.length; i++) {
            try {
                return new Intl.DateTimeFormat(locales[i], { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(d);
            } catch (e) { /* try next */ }
        }
        return '';
    }

    // ===== Timer (order-within countdown to today's cutoff) ===================

    /**
     * Seconds remaining until TODAY's cutoffHour in the warehouse timezone.
     * Returns null when today is not a configured workingDay OR now is at/after
     * the cutoff (i.e. the timer must not run). Reuses the same warehouseNowParts
     * source of truth as computeEstimate, now including minute+second.
     */
    function secondsUntilCutoff(cfg) {
        var parts = warehouseNowParts(cfg.timezone);
        if (!parts) return null;
        var cursor = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12, 0, 0));
        if (isNaN(cursor.getTime())) return null;
        var working = normalizeWorkingDays(cfg.workingDays);
        if (working.indexOf(isoWeekday(cursor)) === -1) return null; // not a working day today
        var cutoff = isFiniteNum(cfg.cutoffHour) ? cfg.cutoffHour : 14;
        var minute = isFiniteNum(parts.minute) ? parts.minute : 0;
        var second = isFiniteNum(parts.second) ? parts.second : 0;
        var nowSecs = parts.hour * 3600 + minute * 60 + second;
        var cutoffSecs = cutoff * 3600;
        var remaining = cutoffSecs - nowSecs;
        if (remaining <= 0) return null; // at/after cutoff
        return remaining;
    }

    /**
     * "03:42:18" — zero-padded HH:MM:SS for the side countdown BOX (always < 24h
     * since it counts to today's cutoff). Reads as a digital clock.
     */
    function formatClock(totalSecs) {
        var s = Math.max(0, Math.floor(totalSecs));
        var h = Math.floor(s / 3600);
        var m = Math.floor((s % 3600) / 60);
        var sec = s % 60;
        function p(n) { return n < 10 ? '0' + n : '' + n; }
        return p(h) + ':' + p(m) + ':' + p(sec);
    }

    // ===== Template + render ==================================================

    var STYLE_ID = 'agb-delivery-estimate-style';

    function ensureStyles() {
        if (document.getElementById(STYLE_ID)) return;
        var style = document.createElement('style');
        style.id = STYLE_ID;
        style.textContent = [
            // Outer rectangle: message on the left, the countdown BOX on the
            // right AT THE SAME LEVEL when there's room; on a narrow product
            // column the box wraps BELOW and CENTERS (justify-content:center).
            // The message block grows (flex:1 1 14rem) so it fills the row and
            // keeps the box hard-right until it no longer fits, then the box
            // drops to its own line and centers.
            '.agb-delivery-estimate{display:flex;align-items:center;justify-content:center;',
            'gap:.6rem .85rem;flex-wrap:wrap;margin-top:.75rem;padding:.7rem .9rem;',
            'border:1px solid rgba(0,0,0,.12);border-radius:2px;',
            'font-size:.8125rem;line-height:1.45;color:inherit;}',
            '.agb-delivery-estimate__main{display:flex;align-items:flex-start;gap:.6em;min-width:0;flex:1 1 14rem;}',
            '.agb-delivery-estimate__icon{flex:none;width:1.25em;height:1.25em;margin-top:.15em;opacity:.7;}',
            '.agb-delivery-estimate__icon svg{display:block;width:100%;height:100%;}',
            // The single message line: semibold copy at a comfortable reading
            // size; the {threshold} amount gets its own emphasis below.
            '.agb-delivery-estimate__msg{display:block;min-width:0;font-weight:600;',
            'font-size:.875rem;line-height:1.35;letter-spacing:.005em;}',
            // The threshold amount ("$99") — the number must read at a glance:
            // heavier, tabular figures (no digit jitter), brand red to draw the eye.
            '.agb-de-amount{font-weight:700;font-variant-numeric:tabular-nums;',
            'font-feature-settings:"tnum";white-space:nowrap;color:#8f1814;}',
            // The side countdown box: a caption over the live digits.
            '.agb-delivery-estimate__timer{flex:none;display:flex;flex-direction:column;',
            'align-items:center;gap:.15em;padding:.4rem .65rem;border:1px solid rgba(0,0,0,.14);',
            'border-radius:2px;background:rgba(0,0,0,.03);}',
            '.agb-de-timer-caption{font-size:.6rem;font-weight:600;text-transform:uppercase;',
            'letter-spacing:.06em;opacity:.6;white-space:nowrap;}',
            '.agb-de-timer{font-variant-numeric:tabular-nums;font-feature-settings:"tnum";',
            'font-weight:700;font-size:1.05rem;line-height:1;letter-spacing:.02em;',
            'white-space:nowrap;color:#8f1814;}'
        ].join('');
        document.head.appendChild(style);
    }

    // Static markup only — config strings are set via textContent below.
    var ICON_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
        '<path d="M1 7h13v10H1z"></path>' +
        '<path d="M14 10h4l4 4v3h-8"></path>' +
        '<circle cx="6" cy="17.5" r="1.8"></circle>' +
        '<circle cx="18" cy="17.5" r="1.8"></circle>' +
        '</svg>';

    /**
     * Compute the date variables for this render ONCE: {date1, date2, country}.
     *   date1   = earliest arrival date (localized)
     *   date2   = latest arrival date  = start + rangePadDays (localized)
     *   country = the shopper's 2-letter ISO country CODE (e.g. "US", "IL",
     *             "FR"); '' when undetected. Lets the text use {country} instead
     *             of a hardcoded "US" — "Free {country} Shipping" -> "Free US
     *             Shipping" for a US shopper, "Free FR Shipping" for a French one.
     * Both dates are always computed (no range/single split). Returns null on any
     * failure (fail-safe: render nothing). A merchant wanting a single date just
     * uses {date1} alone in the text.
     */
    // {country} display name. US and GB stay as the short familiar forms Dorian
    // asked for ("US" / "UK"); every other country shows its FULL name, localized
    // to the document language via Intl.DisplayNames when available (so the EU
    // fr/de store shows "Allemagne"/"Kanada"), falling back to a baked English
    // map, then the raw code. '' for an empty code.
    var COUNTRY_NAME_OVERRIDES = { US: 'US', GB: 'UK' };
    var COUNTRY_NAME_EN = {
        AE: 'United Arab Emirates', AU: 'Australia', BR: 'Brazil', CA: 'Canada',
        HK: 'Hong Kong', JP: 'Japan', KR: 'South Korea', MX: 'Mexico',
        NZ: 'New Zealand', SG: 'Singapore', TH: 'Thailand', TW: 'Taiwan',
        ZA: 'South Africa', AT: 'Austria', BE: 'Belgium', BG: 'Bulgaria',
        HR: 'Croatia', CZ: 'Czechia', DK: 'Denmark', EE: 'Estonia', FR: 'France',
        DE: 'Germany', HU: 'Hungary', IT: 'Italy', LT: 'Lithuania',
        LU: 'Luxembourg', NL: 'Netherlands', PL: 'Poland', PT: 'Portugal',
        RO: 'Romania', SK: 'Slovakia', SI: 'Slovenia', ES: 'Spain', LV: 'Latvia',
        IS: 'Iceland', CY: 'Cyprus', GR: 'Greece'
    };
    function countryDisplayName(code, lang) {
        if (!code) return '';
        if (COUNTRY_NAME_OVERRIDES[code]) return COUNTRY_NAME_OVERRIDES[code];
        try {
            if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
                var dn = new Intl.DisplayNames([lang || 'en'], { type: 'region' });
                var name = dn.of(code);
                if (name && name !== code) return name;
            }
        } catch (e) {}
        return COUNTRY_NAME_EN[code] || code;
    }

    function computeDateVars(cfg) {
        var lang = docLang();
        var code = resolveEffectiveCode(cfg);
        var days = effectiveDays(cfg, code);
        var est = computeEstimate(cfg, days.ship, days.transit);
        if (!est) return null;
        var date1 = formatDate(est.start, lang);
        var date2 = formatDate(est.end, lang);
        if (!date1 || !date2) return null;
        // {country} = the shopper's country NAME (US/UK kept short; others full,
        // localized). Driven by the effective code, so "Free {country} Shipping"
        // is dynamic. '' when undetected with no fallback (caller drops it).
        var country = countryDisplayName(code, lang);
        _dbg(LOG_PREFIX, 'estimate', {
            env: DELIV_ENV, country: code || '(unknown)',
            shipDays: days.ship, transitDays: days.transit,
            arrives: date1 + ' - ' + date2
        });
        return { date1: date1, date2: date2, country: country };
    }

    // ===== Text resolution (lang -> en -> hardcoded default) =================

    function docLang() {
        try {
            return ((document.documentElement && document.documentElement.lang) || 'en')
                .split('-')[0].toLowerCase() || 'en';
        } catch (e) { return 'en'; }
    }

    // tl[key] ?? en[key] ?? DEFAULT_TEXT[lang][key] ?? DEFAULT_TEXT.en[key] — an
    // EXPLICIT '' in config wins (typeof '' === 'string'), so "empty = hide" is
    // honored; only a truly absent key falls through to the baked default (by
    // document language, then en).
    function textFor(cfg, key) {
        var lang = docLang();
        try {
            var t = cfg && cfg.text;
            if (t && typeof t === 'object') {
                var tl = (t[lang] && typeof t[lang] === 'object') ? t[lang] : null;
                if (tl && typeof tl[key] === 'string') return tl[key];
                var en = (t.en && typeof t.en === 'object') ? t.en : null;
                if (en && typeof en[key] === 'string') return en[key];
            }
        } catch (e) {}
        var dl = DEFAULT_TEXT[lang];
        if (dl && typeof dl[key] === 'string') return dl[key];
        return (DEFAULT_TEXT.en && typeof DEFAULT_TEXT.en[key] === 'string') ? DEFAULT_TEXT.en[key] : '';
    }

    // ===== Free-shipping rules ================================================

    // Absent field -> the ROW default rule. A present-but-empty array (or one of
    // all-invalid entries) stays empty = an intentional "no free-shipping offer".
    function normalizeRules(rules) {
        if (!Array.isArray(rules)) return DEFAULT_FREE_SHIPPING_RULES.slice();
        var out = [];
        for (var i = 0; i < rules.length; i++) {
            var r = rules[i];
            if (!r || typeof r !== 'object' || Array.isArray(r) || !Array.isArray(r.countries)) continue;
            var cs = [];
            for (var j = 0; j < r.countries.length; j++) {
                var c = r.countries[j];
                if (typeof c === 'string' && c.trim()) cs.push(c.trim().toUpperCase());
            }
            var th = r.threshold;
            if (typeof th === 'string' && th.trim() !== '') th = Number(th);
            if (cs.length && isFiniteNum(th)) out.push({ countries: cs, threshold: th });
        }
        return out;
    }

    // First rule whose (upper-cased) countries[] contains the shopper code.
    function firstMatchingRule(rules, code) {
        if (!code) return null;
        var up = String(code).toUpperCase();
        for (var i = 0; i < rules.length; i++) {
            if (rules[i].countries.indexOf(up) !== -1) return rules[i];
        }
        return null;
    }

    function readAnchorPrice(anchor) {
        try {
            var raw = anchor.getAttribute('data-agb-de-price');
            if (raw === null || raw === '') return null;
            var n = parseInt(raw, 10);
            if (isFiniteNum(n) && n >= 0) return n; // cents
        } catch (e) {}
        return null;
    }

    function readAnchorCurrency(anchor) {
        try {
            var c = anchor.getAttribute('data-agb-de-currency');
            if (typeof c === 'string' && /^[A-Za-z]{3}$/.test(c.trim())) return c.trim().toUpperCase();
        } catch (e) {}
        return 'USD';
    }

    // Out-of-stock gate: the widget must not promise delivery on an unavailable
    // variant. Explicit "false"/"0"/"no" = out of stock; missing / "true" /
    // anything else = in stock (fail-open, so a product without the attribute is
    // never hidden by mistake).
    function isAnchorAvailable(anchor) {
        try {
            var a = anchor.getAttribute('data-agb-de-available');
            if (a === 'false' || a === '0' || a === 'no') return false;
        } catch (e) {}
        return true;
    }

    function formatThreshold(threshold, currency, lang) {
        try {
            return new Intl.NumberFormat(lang || 'en', {
                style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0
            }).format(threshold);
        } catch (e) {
            return String(threshold);
        }
    }

    /**
     * Build the WHOLE message for one anchor, or null when the widget must be
     * HIDDEN for this shopper (fail-safe: render nothing):
     *   freeShippingEnabled===false  -> null (hide)
     *   no matching country rule      -> null (hide)
     *   price >= rule.threshold       -> aboveText (qualifies for free shipping)
     *   price < threshold OR unknown  -> belowText
     *   chosen text empty             -> null (hide)
     * {date1}/{date2}/{country} are always filled; {threshold} is filled with the
     * store-currency amount (really only used in belowText). Returns {text, amount}
     * where `amount` is the formatted threshold ONLY on the belowText branch when
     * the rendered text actually contains it (so fillShippingSpan can emphasize
     * it), else null.
     */
    function buildMessage(cfg, anchor, rule, dateVars) {
        try {
            var lang = docLang();
            var date1 = (dateVars && dateVars.date1) || '';
            var date2 = (dateVars && dateVars.date2) || '';
            var country = (dateVars && dateVars.country) || '';
            // Fill {date1}/{date2}/{country} then collapse double-spaces / stray
            // punctuation seams (as the old fillTemplate did). {threshold} is
            // handled by the caller (only the belowText branch carries it).
            function render(tpl) {
                return String(tpl)
                    .replace(/\{date1\}/g, date1)
                    .replace(/\{date2\}/g, date2)
                    .replace(/\{country\}/g, country)
                    .replace(/\s{2,}/g, ' ').replace(/\s+([,.!?])/g, '$1').trim();
            }

            // No free-shipping CONTEXT — free shipping is off globally, OR the
            // shopper's country is in NO rule. Show the delivery-only line
            // (noShippingText); an EMPTY noShippingText keeps the old behaviour
            // of hiding the widget entirely for those shoppers.
            if (cfg.freeShippingEnabled === false || !rule) {
                var noTpl = textFor(cfg, 'noShippingText');
                if (typeof noTpl !== 'string' || !noTpl) return null;
                var noText = render(String(noTpl).replace(/\{threshold\}/g, ''));
                return noText ? { text: noText, amount: null } : null;
            }

            // A rule matches -> aboveText (qualifies) or belowText (below/unknown).
            var priceCents = readAnchorPrice(anchor);
            var currency = readAnchorCurrency(anchor);
            var qualifies = (priceCents !== null && priceCents >= rule.threshold * 100);
            var tpl = textFor(cfg, qualifies ? 'aboveText' : 'belowText');
            if (typeof tpl !== 'string' || !tpl) return null;
            var amount = formatThreshold(rule.threshold, currency, lang);
            var text = render(String(tpl).replace(/\{threshold\}/g, amount));
            if (!text) return null;
            // Emphasize the amount only on the belowText branch, and only when the
            // rendered text actually contains it.
            var emphasize = (!qualifies && text.indexOf(amount) !== -1) ? amount : null;
            return { text: text, amount: emphasize };
        } catch (e) { return null; }
    }

    /**
     * Paint one message into `span`, wrapping the threshold amount (if any) in an
     * `.agb-de-amount` element so the number stands out. Config strings only ever
     * reach the DOM via textContent / text nodes (no innerHTML).
     */
    function fillShippingSpan(span, sl) {
        try {
            while (span.firstChild) span.removeChild(span.firstChild);
            var text = (sl && sl.text) || '';
            var amount = (sl && sl.amount) || null;
            var idx = amount ? text.indexOf(amount) : -1;
            if (idx === -1) { span.textContent = text; return; }
            span.appendChild(document.createTextNode(text.slice(0, idx)));
            var em = document.createElement('span');
            em.className = 'agb-de-amount';
            em.textContent = amount;
            span.appendChild(em);
            span.appendChild(document.createTextNode(text.slice(idx + amount.length)));
        } catch (e) {
            try { span.textContent = (sl && sl.text) || ''; } catch (e2) {}
        }
    }

    /**
     * The caption for the side countdown box — a BAKED localized default (no
     * longer derived from any config text): resolved by document language, with
     * an en fallback.
     */
    function timerCaption() {
        var lang = docLang();
        if (typeof DEFAULT_TIMER_CAPTION[lang] === 'string' && DEFAULT_TIMER_CAPTION[lang]) {
            return DEFAULT_TIMER_CAPTION[lang];
        }
        return DEFAULT_TIMER_CAPTION.en;
    }

    // ===== Shared countdown interval =========================================

    function stopTimer() {
        if (_timerIntervalId !== null) {
            try { clearInterval(_timerIntervalId); } catch (e) {}
            _timerIntervalId = null;
        }
        _timer = null;
    }

    function tickTimer() {
        if (!_timer) return;
        var secs = Math.floor((_timer.targetEpoch - Date.now()) / 1000);
        if (secs <= 0) {
            var onExpire = _timer.onExpire;
            stopTimer();
            if (typeof onExpire === 'function') { try { onExpire(); } catch (e) {} }
            return;
        }
        var txt = formatClock(secs);
        for (var i = 0; i < _timer.spans.length; i++) {
            try { _timer.spans[i].textContent = txt; } catch (e) {}
        }
    }

    function startTimer(targetEpoch, spans, onExpire) {
        stopTimer(); // guard duplicates / clear on re-render
        _timer = { targetEpoch: targetEpoch, spans: spans, onExpire: onExpire };
        tickTimer(); // paint immediately
        try { _timerIntervalId = setInterval(tickTimer, 1000); } catch (e) { _timerIntervalId = null; }
    }

    // ===== Render (one message + optional countdown box) =====================

    /**
     * (Re)render one anchor: icon + the SINGLE message line inside
     * `.agb-delivery-estimate__main`, plus a countdown BOX on the RIGHT when
     * `timer` is non-null (caption + live `.agb-de-timer` digits). Renders
     * NOTHING (leaves the anchor empty) when the variant is out of stock OR
     * `message` is null (no country rule / free shipping disabled). Returns the
     * live digits span so the shared interval can update it (null when no box).
     */
    function renderAnchor(anchor, message, timer) {
        try {
            // Idempotent: clear any previous render (cache publish followed by a
            // fresh 200 re-render, or a timer expiry fall-back).
            while (anchor.firstChild) anchor.removeChild(anchor.firstChild);
            // Out of stock -> render nothing (no delivery promise on an
            // unavailable variant). The anchor stays empty.
            if (!isAnchorAvailable(anchor)) return null;
            // No message (no matching rule / free shipping disabled) -> hide the
            // whole widget for this shopper.
            if (!message) return null;

            var wrap = document.createElement('div');
            wrap.className = 'agb-delivery-estimate';

            // LEFT: icon + the single message line.
            var main = document.createElement('div');
            main.className = 'agb-delivery-estimate__main';

            var icon = document.createElement('span');
            icon.className = 'agb-delivery-estimate__icon';
            icon.innerHTML = ICON_SVG;
            main.appendChild(icon);

            var msg = document.createElement('span');
            msg.className = 'agb-delivery-estimate__msg';
            fillShippingSpan(msg, message); // {threshold} emphasized via .agb-de-amount
            main.appendChild(msg);
            wrap.appendChild(main);

            // RIGHT: the countdown box (only when the timer is active).
            var timerSpan = null;
            if (timer) {
                var box = document.createElement('div');
                box.className = 'agb-delivery-estimate__timer';
                if (timer.caption) {
                    var cap = document.createElement('span');
                    cap.className = 'agb-de-timer-caption';
                    cap.textContent = timer.caption;
                    box.appendChild(cap);
                }
                timerSpan = document.createElement('span');
                timerSpan.className = 'agb-de-timer';
                timerSpan.textContent = timer.initial || '';
                box.appendChild(timerSpan);
                wrap.appendChild(box);
            }

            anchor.appendChild(wrap);
            return timerSpan;
        } catch (e) { return null; } // fail-safe: leave the anchor empty
    }

    // Render every anchor for one pass; returns the collected live timer digit
    // spans (empty when `timer` is null). The message is built PER ANCHOR here so
    // a re-render after a variant/price change reflects the new price (above vs
    // below the free-shipping threshold). `dateVars` is shared across anchors.
    function renderAnchors(anchors, cfg, dateVars, timer) {
        var fsEnabled = cfg.freeShippingEnabled !== false;
        var rule = fsEnabled ? firstMatchingRule(normalizeRules(cfg.freeShippingRules), resolveEffectiveCode(cfg)) : null;
        var spans = [];
        for (var i = 0; i < anchors.length; i++) {
            var message = buildMessage(cfg, anchors[i], rule, dateVars);
            var span = renderAnchor(anchors[i], message, timer);
            if (span) spans.push(span);
        }
        return spans;
    }

    function renderAll(cfg) {
        var anchors = document.querySelectorAll('.delivery-estimate');
        if (!anchors.length) return; // not a PDP (or anchors not shipped yet)
        // ALLOW-LIST gate: a shopper outside the shipped countries sees nothing.
        if (!isShippable(cfg, resolveEffectiveCode(cfg))) {
            _dbg(LOG_PREFIX, 'country not in shipCountries -- widget hidden');
            return;
        }
        var dateVars = computeDateVars(cfg);
        if (!dateVars) return; // fail-safe: no arrival dates -> render nothing
        ensureStyles();
        stopTimer(); // clear any interval from a prior render before re-painting

        // Side countdown BOX: shown before today's cutoff on a working day.
        var timer = null;
        if (cfg.timerEnabled !== false) {
            var secs = secondsUntilCutoff(cfg);
            if (isFiniteNum(secs) && secs > 0) {
                timer = {
                    caption: timerCaption(),
                    initial: formatClock(secs),
                    targetEpoch: Date.now() + secs * 1000
                };
            }
        }

        var spans = renderAnchors(anchors, cfg, dateVars, timer);

        if (timer && spans.length) {
            startTimer(timer.targetEpoch, spans, function () {
                // Countdown hit 0: re-render without the box (the message is
                // unchanged; just the timer box drops away).
                renderAnchors(anchors, cfg, dateVars, null);
            });
        }
        _dbg(LOG_PREFIX, 'rendered into', anchors.length, 'anchor(s), env=' + DELIV_ENV,
            timer ? '(timer active)' : '(no timer)');
    }

    // Re-read variant price + refresh the message in place (best-effort), so the
    // running countdown box is never disturbed. The above/below text can flip
    // when the new price crosses the free-shipping threshold; the dates are
    // recomputed but variant-independent.
    function updateMessage(anchor, cfg) {
        try {
            var msg = anchor.querySelector('.agb-delivery-estimate__msg');
            if (!msg) return;
            var dateVars = computeDateVars(cfg);
            if (!dateVars) return;
            var fsEnabled = cfg.freeShippingEnabled !== false;
            var rule = fsEnabled ? firstMatchingRule(normalizeRules(cfg.freeShippingRules), resolveEffectiveCode(cfg)) : null;
            var message = buildMessage(cfg, anchor, rule, dateVars); // object | null
            if (message) fillShippingSpan(msg, message);
            // A null message here would mean rule/enabled changed (not a variant
            // event); leave the existing paint rather than half-hiding. The OOS
            // branch in onVariantChange handles the hide case.
        } catch (e) { /* best-effort */ }
    }

    function onVariantChange(e) {
        try {
            if (!_lastCfg) return; // nothing valid rendered yet
            var v = e && e.detail && e.detail.variant;
            var priceCents = (v && typeof v.price === 'number' && isFinite(v.price)) ? v.price : null;
            // available: false only when the event explicitly says so; unknown -> leave as-is.
            var available = (v && typeof v.available === 'boolean') ? v.available : null;
            var anchors = document.querySelectorAll('.delivery-estimate');
            for (var i = 0; i < anchors.length; i++) {
                if (priceCents !== null) {
                    try { anchors[i].setAttribute('data-agb-de-price', String(priceCents)); } catch (e2) {}
                }
                if (available !== null) {
                    try { anchors[i].setAttribute('data-agb-de-available', available ? 'true' : 'false'); } catch (e3) {}
                }
            }

            if (available === false) {
                // Switched to an out-of-stock variant -> hide the whole widget.
                stopTimer();
                for (var j = 0; j < anchors.length; j++) {
                    try { while (anchors[j].firstChild) anchors[j].removeChild(anchors[j].firstChild); } catch (e4) {}
                }
                return;
            }

            // In stock (or unknown). If the widget is currently hidden (was OOS,
            // or first not-yet-rendered), do a full re-render so it reappears
            // WITH its countdown; otherwise just refresh the message for the new
            // price so the running countdown is never disturbed.
            var anyRendered = false;
            for (var k = 0; k < anchors.length; k++) {
                if (anchors[k].firstChild) { anyRendered = true; break; }
            }
            if (!anyRendered) {
                renderAll(_lastCfg);
            } else {
                for (var m = 0; m < anchors.length; m++) {
                    updateMessage(anchors[m], _lastCfg);
                }
            }
        } catch (err) { /* best-effort */ }
    }

    // ===== Config loading (gear-suggestions-config.js pattern) ===============

    /**
     * Validate + render a config document. No-ops unless it is schemaVersion 1
     * with the master `enabled` flag on — a disabled/invalid config must never
     * change the page.
     */
    function applyConfig(cfg) {
        try {
            if (!cfg || typeof cfg !== 'object') return;
            if (cfg.schemaVersion !== 1) return;
            if (cfg.enabled !== true) return;
            _lastCfg = cfg; // remembered for the variant-change line-2 refresh
            renderAll(cfg);
        } catch (e) { /* fail-safe: page unchanged */ }
    }

    function loadCachedConfig() {
        try {
            var raw = localStorage.getItem(LS_CONFIG_KEY);
            if (!raw) return;
            var parsed = JSON.parse(raw);
            if (parsed && parsed.schemaVersion === 1) {
                applyConfig(parsed);
            }
        } catch (e) { /* ignore */ }
    }

    function fetchConfigFromAPI() {
        var base = window.AGB_DELIVERY_ESTIMATE_CONFIG_URL;
        if (!base) {
            _dbg(LOG_PREFIX, 'no AGB_DELIVERY_ESTIMATE_CONFIG_URL set -- skipping fetch');
            return;
        }
        var endpoint = DELIV_ENV === 'preview'
            ? base + (base.indexOf('?') === -1 ? '?' : '&') + 'env=preview'
            : base;
        try {
            var lastFetched = parseInt(localStorage.getItem(LS_CONFIG_AT) || '0', 10);
            if (Date.now() - lastFetched < CONFIG_TTL_MS) return;

            var etag = localStorage.getItem(LS_CONFIG_ETAG);
            var headers = { 'Accept': 'application/json' };
            if (etag) headers['If-None-Match'] = etag;
            // Send this store's region explicitly when the theme declares it
            // (window.AGB_STORE_REGION): a preview theme runs on a myshopify /
            // shopifypreview host the backend can't map from the domain.
            try {
                var rgn = window.AGB_STORE_REGION || '';
                if (rgn) headers['X-Store-Region'] = rgn;
            } catch (e) {}

            fetch(endpoint, { headers: headers })
                .then(function (res) {
                    try { localStorage.setItem(LS_CONFIG_AT, String(Date.now())); } catch (e) {}
                    if (res.status === 304) return null; // cached copy already applied at boot
                    if (!res.ok) return null;
                    return res.json().then(function (body) {
                        if (!body || !body.config) return null;
                        var newCfg = body.config;
                        if (newCfg.schemaVersion !== 1) return null;
                        // Persist whatever valid document the panel served —
                        // including a master-disabled one, so the NEXT page load
                        // correctly renders nothing. applyConfig below still
                        // refuses to render it.
                        try { localStorage.setItem(LS_CONFIG_KEY, JSON.stringify(newCfg)); } catch (e) {}
                        try {
                            var newEtag = res.headers.get('ETag');
                            if (newEtag) localStorage.setItem(LS_CONFIG_ETAG, newEtag);
                        } catch (e) {}
                        applyConfig(newCfg); // fresh 200 re-renders over the cached paint
                        return null;
                    });
                })
                .catch(function () { /* network failure: keep cached render */ });
        } catch (e) { /* fail-safe */ }
    }

    // ===== Init ===============================================================

    function init() {
        loadCachedConfig();
        // Async refresh; a fresh 200 re-renders the anchors in place.
        fetchConfigFromAPI();
        // Refresh the message when the shopper switches variant (bubbles to
        // document from both the stock + gear-builder pickers). The countdown box
        // is variant-independent and left untouched.
        try { document.addEventListener('variant:change', onVariantChange); } catch (e) {}
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
